import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as api from '../services/api';
import { useToast } from '../components/ToastContainer';
import './Notifications.css';

const TYPE_META = {
  info: { label: 'i', tone: 'info' },
  success: { label: '✓', tone: 'success' },
  warning: { label: '!', tone: 'warning' },
  error: { label: '×', tone: 'error' },
  connection: { label: '◎', tone: 'connection' },
  system: { label: '⚙', tone: 'system' },
  ticket_created: { label: 'T', tone: 'info' },
  ticket_reply: { label: 'T', tone: 'success' },
  ticket_status: { label: 'T', tone: 'warning' },
};

const Notifications = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = { limit: 100 };
      if (filter === 'unread') params.unread = 'true';
      const data = await api.getNotifications(params);
      setNotifications(data?.notifications || []);
      setUnreadCount(data?.unread_count || 0);
      setTotal(data?.total || 0);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      if (!silent) showError(t('notifications.loadError', 'Could not load notifications'));
    } finally {
      setLoading(false);
    }
  }, [filter, showError, t]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'read') return notifications.filter((n) => n.is_read);
    return notifications;
  }, [notifications, filter]);

  const handleMarkAsRead = async (id) => {
    const target = notifications.find((n) => n.id === id);
    if (!target || target.is_read) return;
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await api.markNotificationAsRead(id);
    } catch (error) {
      load({ silent: true });
    }
  };

  const handleMarkAll = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    try {
      await api.markAllNotificationsAsRead();
      showSuccess(t('notifications.allMarked', 'All notifications marked as read'));
    } catch (error) {
      load({ silent: true });
      showError(t('notifications.markError', 'Could not mark notifications as read'));
    }
  };

  const handleDelete = async (id) => {
    const target = notifications.find((n) => n.id === id);
    if (!target || busyId === id) return;
    const previous = notifications;
    const previousUnread = unreadCount;
    setBusyId(id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (!target.is_read) setUnreadCount((prev) => Math.max(0, prev - 1));
    setTotal((prev) => Math.max(0, prev - 1));
    try {
      const result = await api.deleteNotification(id);
      if (typeof result?.unread_count === 'number') setUnreadCount(result.unread_count);
    } catch (error) {
      setNotifications(previous);
      setUnreadCount(previousUnread);
      setTotal((prev) => prev + 1);
      showError(t('notifications.deleteError', 'Could not delete notification'));
    } finally {
      setBusyId(null);
    }
  };

  const handleOpen = async (notification) => {
    if (!notification.is_read) await handleMarkAsRead(notification.id);
    if (notification.link) navigate(notification.link);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleString(i18n.language || undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="nt-page">
      <section className="nt-hero">
        <div>
          <p className="nt-kicker">{t('notifications.kicker', 'Inbox')}</p>
          <h1>{t('notifications.title', 'Notifications')}</h1>
          <p className="nt-lead">
            {t(
              'notifications.lead',
              'Connection events, support updates, and system messages in one place.'
            )}
          </p>
        </div>
        <div className="nt-hero-actions">
          {unreadCount > 0 && (
            <button type="button" className="nt-btn ghost" onClick={handleMarkAll}>
              {t('notifications.markAll', 'Mark all read')}
            </button>
          )}
        </div>
      </section>

      <section className="nt-stats">
        <article>
          <span>{t('notifications.total', 'Total')}</span>
          <strong>{total}</strong>
        </article>
        <article>
          <span>{t('notifications.unreadLabel', 'Unread')}</span>
          <strong>{unreadCount}</strong>
        </article>
        <article>
          <span>{t('notifications.showing', 'Showing')}</span>
          <strong>{filtered.length}</strong>
        </article>
      </section>

      <div className="nt-toolbar">
        <div className="nt-filters">
          {[
            { key: 'all', label: t('notifications.filter.all', 'All') },
            { key: 'unread', label: t('notifications.filter.unread', 'Unread') },
            { key: 'read', label: t('notifications.filter.read', 'Read') },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              className={`nt-filter ${filter === item.key ? 'is-active' : ''}`}
              onClick={() => setFilter(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <section className="nt-list-panel">
        {loading ? (
          <div className="nt-empty">
            <div className="nt-spinner" />
            <p>{t('common.loading', 'Loading…')}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="nt-empty">
            <h3>{t('notifications.empty', 'No notifications yet')}</h3>
            <p>{t('notifications.emptyDesc', 'New activity will appear here automatically.')}</p>
          </div>
        ) : (
          <ul className="nt-list">
            {filtered.map((notification) => {
              const meta = TYPE_META[notification.type] || TYPE_META.info;
              return (
                <li
                  key={notification.id}
                  className={`nt-item ${!notification.is_read ? 'unread' : ''} ${
                    busyId === notification.id ? 'is-busy' : ''
                  }`}
                >
                  <button type="button" className="nt-item-main" onClick={() => handleOpen(notification)}>
                    <span className={`nt-icon tone-${meta.tone}`}>{meta.label}</span>
                    <span className="nt-copy">
                      <strong>{notification.title}</strong>
                      <em>{notification.message}</em>
                      <small>{formatTime(notification.created_at)}</small>
                    </span>
                    {!notification.is_read && <span className="nt-dot" />}
                  </button>
                  <div className="nt-item-actions">
                    {!notification.is_read && (
                      <button type="button" onClick={() => handleMarkAsRead(notification.id)}>
                        {t('notifications.markRead', 'Mark read')}
                      </button>
                    )}
                    <button type="button" className="danger" onClick={() => handleDelete(notification.id)}>
                      {t('notifications.delete', 'Delete')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default Notifications;
